import React, { useState, useEffect } from "react";
import Icon from './child/ComplaintIcon';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NatureSelect from './child/NatureSelect';
import { priorityMeta } from '../utils/priority';
import { ShieldCheck, IdentificationCard, MagnifyingGlass, ArrowRight, Info } from '@phosphor-icons/react';
import './DuplicateCheck.css';
import './ComplaintRegistration.css';
import RegistrationSelect from './child/RegistrationSelect';

const CNIC_PATTERN = /^\d{5}-\d{7}-\d$/;
const formatCnic = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 13);
  return [digits.slice(0, 5), digits.slice(5, 12), digits.slice(12)].filter(Boolean).join('-');
};

const ComplainFormPage = () => {
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;

  // Define formatDateForInput before useState
  const formatDateForInput = (date) => {
    const localDate = new Date(date);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [authUserId, setAuthUserId] = useState("");
  const [minimumComplaintTime, setMinimumComplaintTime] = useState(() => formatDateForInput(new Date()));
  const [complaintTimeEdited, setComplaintTimeEdited] = useState(false);
  const [formData, setFormData] = useState({
    dept_id: "",
    district_id: "",
    complaint_nature_id: "",
    complaint_nearby_place: "",
    complaint_detail: "",
    complainant_name: "",
    complainant_address: "",
    complainant_phone: "",
    complainant_email: "",
    complaint_time: formatDateForInput(new Date()),
    complaint_additional_remarks: "",
    complainant_phone_secondry: "",
    other_nature: "",
    received_by: "",
    status: "Open",
    priority_level: "",
    cnic: "",
    complaint_source: "",
    complaint_reffered_by: "",
    complainant_tehsil: "",
    complainant_district: "",
    complaint_tehsil: "",
    complaint_district: "",
    complaint_address: ""
  });

  const [departments, setDepartments] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [complainantTehsils, setComplainantTehsils] = useState([]); // Separate state for Complainant Tehsils
  const [complaintTehsils, setComplaintTehsils] = useState([]); // Separate state for Complaint Tehsils
  const [complaintNatures, setComplaintNatures] = useState([]);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [anonymize, setAnonymize] = useState(false);
  // "Informative call" flag — recorded for the record only, never dispatched.
  const [informative, setInformative] = useState(false);

  // CNIC duplicate-check gate shown before the Complaint Management Form. The
  // CRO searches a complainant's CNIC to see any complaints already filed under
  // it (avoiding duplicate entries), then proceeds to (or skips to) the form.
  const [showCnicGate, setShowCnicGate] = useState(true);
  const [cnicQuery, setCnicQuery] = useState("");
  const [cnicError, setCnicError] = useState("");
  const [cnicResults, setCnicResults] = useState([]);
  const [cnicSearching, setCnicSearching] = useState(false);
  const [cnicSearched, setCnicSearched] = useState(false);

  const handleCnicSearch = async () => {
    if (cnicSearching) return;
    const cnic = cnicQuery.trim();
    if (!CNIC_PATTERN.test(cnic)) {
      setCnicError("Enter all 13 CNIC digits in the format 12345-6789012-3.");
      return;
    }
    setCnicError("");
    try {
      setCnicSearching(true);
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_BASE_URL}/backend/complaints/get-complaint-repitition`,
        { cnic },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        setCnicResults(Array.isArray(res.data.data) ? res.data.data : []);
        setCnicSearched(true);
      } else {
        toast.error(res.data?.message || "Search failed.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error searching complaints.");
    } finally {
      setCnicSearching(false);
    }
  };

  // Reveal the form. When `prefill` is true the searched CNIC is carried into
  // the form so the CRO doesn't retype it.
  const proceedToForm = (prefill) => {
    if (prefill && cnicQuery.trim()) {
      setFormData((prev) => ({ ...prev, cnic: cnicQuery.trim() }));
    }
    setShowCnicGate(false);
  };

  // Per-complaint "append additional remarks" drafts + in-flight save id.
  const [remarkDrafts, setRemarkDrafts] = useState({});
  const [savingRemarkId, setSavingRemarkId] = useState(null);

  const handleAppendRemark = async (complaintId) => {
    const text = (remarkDrafts[complaintId] || "").trim();
    if (!text) {
      toast.error("Please type the remarks to append.");
      return;
    }
    try {
      setSavingRemarkId(complaintId);
      const token = localStorage.getItem("token");
      const res = await axios.patch(
        `${API_BASE_URL}/backend/complaints/append-remarks/${complaintId}`,
        { remarks: text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        const updatedRemarks = res.data?.data?.remarks;
        setCnicResults((prev) =>
          prev.map((c) => (c._id === complaintId ? { ...c, remarks: updatedRemarks } : c))
        );
        setRemarkDrafts((prev) => ({ ...prev, [complaintId]: "" }));
        toast.success("Remarks appended to the existing complaint.");
      } else {
        toast.error(res.data?.message || "Failed to append remarks.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error appending remarks.");
    } finally {
      setSavingRemarkId(null);
    }
  };

  const complaintSources = [
    { value: "Helpline", label: "Helpline" },
    { value: "Chatbot", label: "Chatbot" },
    { value: "Whatsapp", label: "Whatsapp" },
    { value: "Social Media", label: "Social Media" },
    { value: "By Post", label: "By Post" },
    { value: "By Hand", label: "By Hand" },
    { value: "By Email", label: "By Email" },
    { value: "Other", label: "Other" }
  ];

  const complaintReferredBy = [
    { value: "Chief Minister's Secretariat", label: "Chief Minister's Secretariat" },
    { value: "Chief Secretariat Office", label: "Chief Secretariat Office" },
    { value: "Administrative Dept (Secretariat)", label: "Administrative Dept (Secretariat)" },
    { value: "Attached Department", label: "Attached Department" },
    { value: "Control Room (Helpline)", label: "Control Room (Helpline)" },
    { value: "Chief Conservator of Forests (PM&E)", label: "Chief Conservator of Forests (PM&E)" },
    { value: "Chief Conservator of Forests (Central)", label: "Chief Conservator of Forests (Central)" },
    { value: "Chief Conservator of Forests (Northern)", label: "Chief Conservator of Forests (Northern)" },
    { value: "Chief Conservator of Forests (Southern)", label: "Chief Conservator of Forests (Southern)" },
    { value: "Secretary Forest", label: "Secretary Forest" },
    { value: "AS PM&E", label: "AS PM&E" },
    { value: "Forest Social Media Team", label: "Forest Social Media Team" },
    { value: "Other", label: "Other" }
  ];

  // Fetch auth user
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user && user._id) {
      setAuthUserId(user._id);
      setFormData(prev => ({ ...prev, received_by: user._id }));
    } else {
      console.error("User not found in localStorage");
    }
  }, []);

  // Fetch Departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/backend/departments/get-all-departments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDepartments(response.data.data);
      } catch (error) {
        toast.error("Failed to fetch departments.");
        console.error("Error fetching departments:", error);
      }
    };
    fetchDepartments();
  }, [API_BASE_URL]);

  // Fetch Districts
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/backend/districts/get-all-districts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDistricts(response.data.data);
      } catch (error) {
        toast.error("Failed to fetch districts.");
        console.error("Error fetching districts:", error);
      }
    };
    fetchDistricts();
  }, [API_BASE_URL]);


  // Fetch Tehsils for Complainant District
  useEffect(() => {
    const fetchTehsils = async () => {
      if (formData.complainant_district) {
        try {
          const token = localStorage.getItem("token");
          const selectedDistrict = districts.find(d => d._id === formData.complainant_district);
          if (!selectedDistrict) {
            setComplainantTehsils([]);
            return;
          }
          const response = await axios.post(
            `${API_BASE_URL}/backend/districts/get-tehsils-from-district`,
            { district: selectedDistrict.name },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );
          setComplainantTehsils(response.data.tehsils || []);
          setFormData(prev => ({ ...prev, complainant_tehsil: "" }));
        } catch (error) {
          toast.error("Failed to fetch tehsils for the complainant district.");
          console.error("Error fetching complainant tehsils:", error);
          setComplainantTehsils([]);
        }
      } else {
        setComplainantTehsils([]);
        setFormData(prev => ({ ...prev, complainant_tehsil: "" }));
      }
    };
    fetchTehsils();
  }, [formData.complainant_district, districts, API_BASE_URL]);

  // Fetch Tehsils for Complaint District
  useEffect(() => {
    const fetchTehsils = async () => {
      if (formData.complaint_district) {
        try {
          const token = localStorage.getItem("token");
          const selectedDistrict = districts.find(d => d._id === formData.complaint_district);
          if (!selectedDistrict) {
            setComplaintTehsils([]);
            return;
          }
          const response = await axios.post(
            `${API_BASE_URL}/backend/districts/get-tehsils-from-district`,
            { district: selectedDistrict.name },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );
          setComplaintTehsils(response.data.tehsils || []);
          setFormData(prev => ({ ...prev, complaint_tehsil: "" }));
        } catch (error) {
          toast.error("Failed to fetch tehsils for the complaint district.");
          console.error("Error fetching complaint tehsils:", error);
          setComplaintTehsils([]);
        }
      } else {
        setComplaintTehsils([]);
        setFormData(prev => ({ ...prev, complaint_tehsil: "" }));
      }
    };
    fetchTehsils();
  }, [formData.complaint_district, districts, API_BASE_URL]);

  // Fetch Complaint Natures
  useEffect(() => {
    const fetchComplaintNatures = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/backend/complaint-natures/get-all-complaint-natures`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setComplaintNatures(response.data.data);
      } catch (error) {
        toast.error("Failed to fetch complaint natures.");
        console.error("Error fetching complaint natures:", error);
      }
    };
    fetchComplaintNatures();
  }, [API_BASE_URL]);

  // Keep the picker cutoff current; an untouched default follows the clock.
  useEffect(() => {
    const timer = setInterval(() => setMinimumComplaintTime(formatDateForInput(new Date())), 10000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!complaintTimeEdited) {
      setFormData((previous) => previous.complaint_time < minimumComplaintTime
        ? { ...previous, complaint_time: minimumComplaintTime } : previous);
    }
  }, [minimumComplaintTime, complaintTimeEdited]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'complaint_time') setComplaintTimeEdited(true);
    if (name === "cnic") {
      const formattedValue = formatCnic(value);
      setFormData({
        ...formData,
        [name]: formattedValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const cnicRegex = /^[0-9]{5}-[0-9]{7}-[0-9]{1}$/;
    if (name === "cnic" && value && !cnicRegex.test(value)) {
      toast.error("Please enter a valid CNIC in the format 12345-6789012-3");
    }
    const pakPhoneRegex = /^(\+92|0)3[0-9]{9}$/;
    if ((name === "complainant_phone" || name === "complainant_phone_secondry") && value && !pakPhoneRegex.test(value)) {
      toast.error("Please enter a valid Pakistani phone number (e.g., +923001234567 or 03001234567)");
    }
  };

  const handleSelectChange = (e) => {
    const { value, name } = e.target;
    // A new district invalidates the tehsil picked for the old one (both are
    // required), so clear it and make the user choose again.
    const resetTehsil =
      name === "complainant_district" ? { complainant_tehsil: "" }
      : name === "complaint_district" ? { complaint_tehsil: "" }
      : {};
    setFormData({ ...formData, [name]: value, ...resetTehsil });

    if (name === "complaint_nature_id") {
      const selectedNature = complaintNatures.find(nature => nature._id === value);
      if (selectedNature && selectedNature.name.toLowerCase() === "other") {
        setShowOtherInput(true);
      } else {
        setShowOtherInput(false);
        setFormData(prev => ({ ...prev, other_nature: "" }));
      }
    }
  };

  // Nature drives priority: selecting a nature sets both the nature id AND the
  // (read-only) priority derived from that nature. The operator never picks a
  // priority directly.
  const handleNatureChange = (natureId) => {
    const selectedNature = complaintNatures.find((n) => n._id === natureId);
    const isOther = selectedNature && selectedNature.name?.toLowerCase() === "other";
    setShowOtherInput(!!isOther);
    setFormData((prev) => ({
      ...prev,
      complaint_nature_id: natureId,
      priority_level: selectedNature?.priority_level || "Medium",
      ...(isOther ? {} : { other_nature: "" }),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const earliest = formatDateForInput(new Date());
    if (!formData.complaint_time || Number.isNaN(new Date(formData.complaint_time).getTime()) || formData.complaint_time < earliest) {
      setMinimumComplaintTime(earliest);
      toast.error('Complaint date and time cannot be in the past. Please select the current or a future time.');
      return;
    }

    // field -> label shown to the user when it is missing (form order).
    const requiredFields = {
      dept_id: "Department",
      complainant_name: "Complainant Name",
      complainant_phone: "Complainant Phone",
      cnic: "Complainant CNIC",
      complainant_address: "Complainant Address",
      complainant_district: "Complainant District",
      complainant_tehsil: "Complainant Tehsil",
      complaint_source: "Complaint Source",
      complaint_nature_id: "Nature of Complaint",
      complaint_nearby_place: "Nearby Landmark",
      complaint_district: "Complaint District",
      complaint_tehsil: "Complaint Tehsil",
      complaint_detail: "Complaint Detail",
    };

    const emptyFields = Object.keys(requiredFields).filter(
      (field) => !String(formData[field] ?? "").trim()
    );
    if (emptyFields.length > 0) {
      const fieldNames = emptyFields.map((field) => requiredFields[field]).join(", ");
      toast.error(`Please fill in the following required details: ${fieldNames}`);
      return;
    }

    const pakPhoneRegex = /^(\+92|0)3[0-9]{9}$/;
    if (!pakPhoneRegex.test(formData.complainant_phone)) {
      toast.error("Please enter a valid Primary phone number (e.g., +923001234567 or 03001234567)");
      return;
    }

    if (formData.complainant_phone_secondry && !pakPhoneRegex.test(formData.complainant_phone_secondry)) {
      toast.error("Please enter a valid Secondary phone number (e.g., +923001234567 or 03001234567)");
      return;
    }

    if (formData.complainant_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.complainant_email)) {
      toast.error("Invalid email format!");
      return;
    }

    const apiData = {
      complainant_name: formData.complainant_name,
      complainant_address: formData.complainant_address,
      complainant_contact_no: formData.complainant_phone,
      complainant_contact_no_sec: formData.complainant_phone_secondry,
      time_of_call: formData.complaint_time,
      complaint_detail: formData.complaint_detail,
      complainant_email: formData.complainant_email,
      dept_id: formData.dept_id,
      complaint_nature_id: formData.complaint_nature_id,
      near_by_place: formData.complaint_nearby_place,
      remarks: formData.complaint_additional_remarks,
      received_by: authUserId,
      // Informative calls are not part of the status lifecycle — they are
      // Resolved immediately (backend enforces this too).
      status: informative ? "Resolved" : "Open",
      priority_level: formData.priority_level,
      complainant_cnic: formData.cnic,
      complaint_source: formData.complaint_source,
      complaint_reffered_by: formData.complaint_reffered_by,
      ...(showOtherInput && { other_nature: formData.other_nature }),
      complainant_tehsil_id: formData.complainant_tehsil,
      complainant_district_id: formData.complainant_district,
      complaint_tehsil_id: formData.complaint_tehsil,
      complaint_district_id: formData.complaint_district,
      anonymize: anonymize,
      // Informative call: recorded for the record only, never dispatched to a
      // forest zone. Hidden from dashboards/counts/lists/reports; shown on the
      // dedicated Informative Calls page. Set via the checkbox below.
      informative: informative,
      complaint_address: formData.complaint_address
    };

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_BASE_URL}/backend/complaints/create-complaint`, apiData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status) {
        const createdData = response.data.data || {};
        const createdNatureObj = complaintNatures.find(n => n._id === formData.complaint_nature_id);
        const createdNatureName = createdNatureObj ? createdNatureObj.name : 'Unknown Nature';

        // Stage 1 — Complaint Registered (Open): SMS to Complainant
        try {
          const contactNo = createdData.complainant_contact_no;
          if (contactNo) {
            const registeredDate = new Date().toLocaleDateString('en-GB');
            const registeredSms = `Dear ${createdData.complainant_name || 'Sir'}, your complaint ${createdData.complaint_number} regarding '${createdNatureName}' has been registered with the Punjab Forest Department on ${registeredDate}. You will be updated as it progresses.`;

            await axios.post('https://smsgateway.pitb.gov.pk/api/send_sms', null, {
              params: {
                sec_key: process.env.REACT_APP_PITB_KEY,
                sms_text: registeredSms,
                phone_no: contactNo,
                sms_language: 'english'
              }
            });
          }
        } catch (smsError) {
          console.log("Error sending SMS:", smsError);
        }

        // Status change: SMS to admin (03379926560)
        try {
          const statusSms = `Dear Sir, a new complaint ${createdData.complaint_number} ('${createdNatureName}') has been registered with status ${createdData.status || apiData.status}.`;
          await axios.post('https://smsgateway.pitb.gov.pk/api/send_sms', null, {
            params: {
              sec_key: process.env.REACT_APP_PITB_KEY,
              sms_text: statusSms,
              phone_no: "03379926560",
              sms_language: 'english'
            }
          });
        } catch (smsError) {
          console.error("Error sending SMS:", smsError);
        }

        toast.success("Complaint Submitted Successfully!");
        setFormData({
          dept_id: "",
          district_id: "",
          complaint_nature_id: "",
          complaint_nearby_place: "",
          complaint_detail: "",
          complainant_name: "",
          complainant_address: "",
          complainant_phone: "",
          complainant_email: "",
          complaint_time: formatDateForInput(new Date()),
          complaint_additional_remarks: "",
          complainant_phone_secondry: "",
          received_by: authUserId,
          status: "Open",
          priority_level: "",
          cnic: "",
          complaint_source: "",
          complaint_reffered_by: "",
          other_nature: "",
          complainant_tehsil: "",
          complainant_district: "",
          complaint_tehsil: "",
          complaint_district: "",
          complaint_address: ""
        });
        setComplainantTehsils([]);
        setComplaintTehsils([]);
        setInformative(false);
        setComplaintTimeEdited(false);
      }
    } catch (error) {
      toast.error("Failed to submit complaint. Please try again.");
      console.error("Error submitting complaint:", error);
    }
  };

  return (
    <div className={`complaint-form col-lg-12 col-md-12 col-sm-12${showCnicGate ? ' duplicate-check-page' : ' complaint-registration-page'}`}>
      <ToastContainer />
      {showCnicGate ? (
        <div className="form-container duplicate-check-card">
          <div className="duplicate-check-layout">
            <div className="duplicate-check-header">
              <span className="duplicate-check-emblem"><ShieldCheck size={32} weight="duotone" aria-hidden="true" /></span>
              <div>
                <span className="duplicate-check-eyebrow">Complaint registration</span>
                <h1>A quick check.<br />A clearer record.</h1>
                <p>Find an existing complaint, add a follow-up, or start a new registration.</p>
                <ol className="duplicate-check-steps">
                  <li><span>01</span><div><strong>Search</strong><small>Look up the complainant&apos;s CNIC</small></div></li>
                  <li><span>02</span><div><strong>Review</strong><small>Check any previous complaints</small></div></li>
                  <li><span>03</span><div><strong>Continue</strong><small>Update a record or create a new one</small></div></li>
                </ol>
              </div>
            </div>

            <div className="duplicate-check-body">
              <span className="duplicate-check-step-label">Before you register</span>
              <h2>Has this been reported before?</h2>

              <p className="duplicate-check-description">
                Enter the complainant&apos;s CNIC to review any existing complaints and avoid duplicate entries.
              </p>

              <div className="duplicate-check-controls">
                <div className="duplicate-check-field">
                  <label htmlFor="duplicate-check-cnic" className="form-label">
                    Complainant CNIC
                  </label>
                  <div className="duplicate-check-input">
                  <IdentificationCard size={23} weight="duotone" aria-hidden="true" />
                  <input
                    id="duplicate-check-cnic"
                    type="text"
                    inputMode="numeric"
                    aria-describedby={cnicError ? 'duplicate-check-error duplicate-check-hint' : 'duplicate-check-hint'}
                    aria-invalid={Boolean(cnicError)}
                    pattern="[0-9]{5}-[0-9]{7}-[0-9]"
                    disabled={cnicSearching}
                    className="form-control"
                    placeholder="12345-6789012-3"
                    value={cnicQuery}
                    onChange={(e) => {
                      const formatted = formatCnic(e.target.value);
                      setCnicQuery(formatted);
                      setCnicError('');
                      setCnicSearched(false);
                      setCnicResults([]);
                    }}
                    onBlur={() => {
                      if (cnicQuery && !CNIC_PATTERN.test(cnicQuery)) {
                        setCnicError('Enter all 13 CNIC digits in the format 12345-6789012-3.');
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCnicSearch(); } }}
                  />
                  </div>
                  {cnicError && <div id="duplicate-check-error" className="duplicate-check-error" role="alert">{cnicError}</div>}
                </div>
                <div className="duplicate-check-actions">
                  <button
                    type="button"
                    className="btn btn-success radius-12 d-inline-flex align-items-center justify-content-center"
                    onClick={handleCnicSearch}
                    disabled={cnicSearching}
                  >
                    <MagnifyingGlass size={18} className="me-2" aria-hidden="true" />
                    {cnicSearching ? "Searching…" : "Search Complaints"}
                  </button>
                  <button
                    type="button"
                    className="btn duplicate-check-skip d-inline-flex align-items-center justify-content-center"
                    onClick={() => proceedToForm(false)}
                  >
                    Skip check &amp; create new complaint <ArrowRight size={18} className="ms-2" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div id="duplicate-check-hint" className="duplicate-check-hint"><Info size={17} weight="duotone" aria-hidden="true" /><span>Use the format 12345-6789012-3. You can also skip this check and continue to the form.</span></div>

              {cnicSearched && (
                <div className="mt-4">
                  {cnicResults.length > 0 ? (
                    <>
                      <div className="alert alert-warning d-flex align-items-center gap-2 mb-3">
                        <Icon icon="mdi:alert-outline" />
                        <span>
                          {cnicResults.length} existing complaint{cnicResults.length > 1 ? "s" : ""} found for this CNIC.
                          Please review before adding a new one.
                        </span>
                      </div>
                      {cnicResults.map((c, idx) => (
                        <div key={c._id} className="bg-white rounded-3 p-3 mb-3 shadow-sm">
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                            <h6 className="mb-0 d-flex align-items-center gap-2">
                              <span className="badge bg-success">#{idx + 1}</span>
                              Complaint {c.complaint_number || "-"}
                              <span className="badge bg-secondary">{c.status || "-"}</span>
                            </h6>
                            <small className="text-muted">
                              {c.time_of_call ? new Date(c.time_of_call).toLocaleString() : "-"}
                            </small>
                          </div>

                          <div className="row g-2 small text-dark">
                            <div className="col-md-4"><strong>Complainant:</strong> {c.complainant_name || "-"}</div>
                            <div className="col-md-4"><strong>CNIC:</strong> {c.complainant_cnic || "-"}</div>
                            <div className="col-md-4"><strong>Phone:</strong> {c.complainant_contact_no || "-"}</div>
                            <div className="col-md-4"><strong>Nature:</strong> {c.complaint_nature_id?.name || c.other_nature || "-"}</div>
                            <div className="col-md-4"><strong>Department:</strong> {c.dept_id?.name || "-"}</div>
                            <div className="col-md-4"><strong>Priority:</strong> {c.priority_level || "-"}</div>
                            <div className="col-md-4"><strong>Source:</strong> {c.complaint_source || "-"}</div>
                            <div className="col-md-4"><strong>Referred By:</strong> {c.complaint_reffered_by || "-"}</div>
                            <div className="col-md-4"><strong>District:</strong> {c.complaint_district_id?.name || c.complainant_district_id?.name || "-"}</div>
                            <div className="col-12"><strong>Address:</strong> {c.complaint_address || c.complainant_address || "-"}</div>
                            <div className="col-12"><strong>Nearby Place:</strong> {c.near_by_place || "-"}</div>
                          </div>

                          <div className="mt-2">
                            <strong className="small">Complaint Detail</strong>
                            <div className="border rounded p-2 bg-light small" style={{ whiteSpace: "pre-wrap" }}>
                              {c.complaint_detail || "No detail provided."}
                            </div>
                          </div>

                          <div className="mt-2">
                            <strong className="small">Additional Remarks</strong>
                            <div className="border rounded p-2 bg-light small" style={{ whiteSpace: "pre-wrap" }}>
                              {c.remarks && String(c.remarks).trim() ? c.remarks : "No additional remarks yet."}
                            </div>
                          </div>

                          <div className="mt-3">
                            <label className="form-label small mb-1 fw-semibold">
                              Append additional remarks to this complaint
                            </label>
                            <textarea
                              className="form-control"
                              rows={2}
                              placeholder="e.g. Complainant called again on the same issue…"
                              value={remarkDrafts[c._id] || ""}
                              onChange={(e) =>
                                setRemarkDrafts((prev) => ({ ...prev, [c._id]: e.target.value }))
                              }
                            />
                            <button
                              type="button"
                              className="btn btn-success radius-12 mt-2 d-inline-flex align-items-center justify-content-center"
                              style={{ width: "auto", height: 40, padding: "0 18px", margin: 0, textTransform: "none", fontSize: "0.9em", fontWeight: 600 }}
                              onClick={() => handleAppendRemark(c._id)}
                              disabled={savingRemarkId === c._id}
                            >
                              <Icon icon="mdi:comment-plus-outline" className="me-1" />
                              {savingRemarkId === c._id ? "Saving…" : "Append Remarks"}
                            </button>
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="btn btn-success radius-12 mt-3 d-inline-flex align-items-center justify-content-center"
                        style={{ width: "auto", height: 46, padding: "0 22px", margin: "12px 0 0", textTransform: "none", fontSize: "1em", fontWeight: 600 }}
                        onClick={() => proceedToForm(true)}
                      >
                        <Icon icon="mdi:plus" className="me-1" /> Proceed to Add New Complaint
                      </button>
                    </>
                  ) : (
                    <div className="alert alert-success d-flex align-items-center justify-content-between flex-wrap gap-2">
                      <span className="d-flex align-items-center gap-2">
                        <Icon icon="mdi:check-circle-outline" /> No previous complaints found for this CNIC.
                      </span>
                      <button
                        type="button"
                        className="btn btn-success radius-12 d-inline-flex align-items-center justify-content-center"
                        style={{ width: "auto", height: 42, padding: "0 18px", margin: 0, textTransform: "none", fontSize: "0.95em", fontWeight: 600 }}
                        onClick={() => proceedToForm(true)}
                      >
                        <Icon icon="mdi:plus" className="me-1" /> Continue to Add Complaint
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="">
            <div className="registration-header">
              <span className="registration-emblem"><ShieldCheck size={32} weight="duotone" aria-hidden="true" /></span>
              <div>
                <span className="registration-eyebrow">Forestry &amp; Wildlife · Complaint registration</span>
                <h1>Register a complaint</h1>
                <p>Provide the complainant&apos;s information and describe the issue below.</p>
              </div>
              <span className="registration-header-tag">New complaint</span>
            </div>
            <div className="registration-section">
              <div className="registration-section-heading"><span>01</span><div><h2>Complainant information</h2><p>Contact and identification details of the person reporting.</p></div></div>
              <hr className="custom-hr"/>
              <div className="row gy-3">
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:office-building" className="text-lg" />
                    Department
                  </label>
                  <RegistrationSelect
                    className="form-select cpf-bg"
                    name="dept_id"
                    value={formData.dept_id}
                    onChange={handleSelectChange}
                    required
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept._id} value={dept._id}>
                        {dept.name}
                      </option>
                    ))}
                  </RegistrationSelect>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:account" className="text-lg" />
                    Complainant Name
                  </label>
                  <input
                    type="text"
                    name="complainant_name"
                    placeholder="Complainant Name"
                    className="form-control cpf-bg"
                    value={formData.complainant_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:phone" className="text-lg" />
                    Complainant Phone
                  </label>
                  <input
                    type="text"
                    name="complainant_phone"
                    placeholder="Phone Number (e.g., +923001234567 or 03001234567)"
                    className="form-control cpf-bg"
                    value={formData.complainant_phone}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    pattern="(\+92|0)3[0-9]{9}"
                    maxLength="13"
                    title="Please enter a valid Pakistani phone number (e.g., +923001234567 or 03001234567)"
                    required
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:phone" className="text-lg" />
                    Complainant Phone Secondary
                  </label>
                  <input
                    type="text"
                    name="complainant_phone_secondry"
                    placeholder="Phone Number Secondary (e.g., +923001234567 or 03001234567)"
                    className="form-control cpf-bg"
                    value={formData.complainant_phone_secondry}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    pattern="(\+92|0)3[0-9]{9}"
                    maxLength="13"
                    title="Please enter a valid Pakistani phone number (e.g., +923001234567 or 03001234567)"
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:card-account-details" className="text-lg" />
                    Complainant CNIC
                  </label>
                  <input
                    type="text"
                    name="cnic"
                    placeholder="CNIC (e.g., 12345-6789012-3)"
                    className="form-control cpf-bg"
                    value={formData.cnic}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    pattern="[0-9]{5}-[0-9]{7}-[0-9]{1}"
                    maxLength="15"
                    title="Please enter a valid CNIC in the format 12345-6789012-3"
                    required
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:email" className="text-lg" />
                    Complainant Email
                  </label>
                  <input
                    type="text"
                    name="complainant_email"
                    placeholder="info@gmail.com"
                    className="form-control cpf-bg"
                    value={formData.complainant_email}
                    onBlur={handleBlur}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:home" className="text-lg" />
                    Complainant Address
                  </label>
                  <input
                    type="text"
                    name="complainant_address"
                    placeholder="Complainant Address"
                    className="form-control cpf-bg"
                    value={formData.complainant_address}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:map-marker" className="text-lg" />
                    Complainant District
                  </label>
                  <RegistrationSelect
                    className="form-select cpf-bg"
                    name="complainant_district"
                    value={formData.complainant_district}
                    onChange={handleSelectChange}
                    required
                  >
                    <option value="">Select District</option>
                    {districts
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((district) => (
                        <option key={district._id} value={district._id}>
                          {district.name}
                        </option>
                      ))}
                  </RegistrationSelect>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:map-marker" className="text-lg" />
                    Complainant Tehsil
                  </label>
                  <RegistrationSelect
                    className="form-select cpf-bg"
                    name="complainant_tehsil"
                    value={formData.complainant_tehsil}
                    onChange={handleSelectChange}
                    required
                  >
                    <option value="">Select Tehsil</option>
                    {complainantTehsils
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((tehsil) => (
                        <option key={tehsil._id} value={tehsil._id}>
                          {tehsil.name}
                        </option>
                      ))}
                  </RegistrationSelect>
                </div>
              </div>
            </div>
            <div className="registration-section registration-section-complaint">
                <div className="registration-section-heading"><span>02</span><div><h2>Complaint information</h2><p>Describe the issue, its location, and how it was reported.</p></div></div>
                <hr className="custom-hr"/>
              <div className="row gy-3">
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:source" className="text-lg" />
                    Complaint Source
                  </label>
                  <RegistrationSelect
                    className="form-select cpf-bg"
                    name="complaint_source"
                    value={formData.complaint_source}
                    onChange={handleSelectChange}
                    required
                  >
                    <option value="">Select Source</option>
                    {complaintSources.map((source) => (
                      <option key={source.value} value={source.value}>
                        {source.label}
                      </option>
                    ))}
                  </RegistrationSelect>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:account-arrow-right" className="text-lg" />
                    Complaint Referred By
                  </label>
                  <RegistrationSelect
                    className="form-select cpf-bg"
                    name="complaint_reffered_by"
                    value={formData.complaint_reffered_by}
                    onChange={handleSelectChange}
                    required
                  >
                    <option value="">Select Reffered By</option>
                    {complaintReferredBy.map((source) => (
                      <option key={source.value} value={source.value}>
                        {source.label}
                      </option>
                    ))}
                  </RegistrationSelect>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:alert-circle-outline" className="text-lg" />
                    Nature of Complaint
                  </label>
                  <NatureSelect
                    natures={complaintNatures}
                    value={formData.complaint_nature_id}
                    onChange={handleNatureChange}
                  />
                  {showOtherInput && (
                    <div className="mt-3">
                      <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                        <Icon icon="mdi:pencil-outline" className="text-lg" />
                        Specify Other Nature
                      </label>
                      <input
                        type="text"
                        className="form-control cpf-bg"
                        name="other_nature"
                        value={formData.other_nature}
                        onChange={handleInputChange}
                        placeholder="Please specify the nature of the complaint"
                      />
                    </div>
                  )}
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:priority-high" className="text-lg" />
                    Priority Level
                  </label>
                  {/* Read-only — derived from the selected nature of complaint. */}
                  <div
                    className="form-control cpf-bg d-flex align-items-center"
                    style={{ minHeight: 38 }}
                  >
                    {formData.priority_level ? (
                      (() => {
                        const m = priorityMeta(formData.priority_level);
                        return (
                          <span
                            className="d-inline-flex align-items-center gap-1"
                            style={{ background: m.color, color: '#fff', borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}
                          >
                            <Icon icon={m.icon} /> {formData.priority_level}
                          </span>
                        );
                      })()
                    ) : (
                      <span className="text-muted" style={{ fontSize: 13 }}>Set automatically from the nature</span>
                    )}
                  </div>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:home" className="text-lg" />
                    Complaint Address
                  </label>
                  <input
                    type="text"
                    name="complaint_address"
                    placeholder="Complaint Address"
                    className="form-control cpf-bg"
                    value={formData.complaint_address}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:map-marker-radius" className="text-lg" />
                    Nearby Landmark of Complainant
                  </label>
                  <input
                    type="text"
                    name="complaint_nearby_place"
                    placeholder="Nearby Landmark of Complainant"
                    className="form-control cpf-bg"
                    value={formData.complaint_nearby_place}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:map-marker" className="text-lg" />
                    Complaint District
                  </label>
                  <RegistrationSelect
                    className="form-select cpf-bg"
                    name="complaint_district"
                    value={formData.complaint_district}
                    onChange={handleSelectChange}
                    required
                  >
                    <option value="">Select District</option>
                    {districts
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((district) => (
                        <option key={district._id} value={district._id}>
                          {district.name}
                        </option>
                      ))}
                  </RegistrationSelect>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:map-marker" className="text-lg" />
                    Complaint Tehsil
                  </label>
                  <RegistrationSelect
                    className="form-select cpf-bg"
                    name="complaint_tehsil"
                    value={formData.complaint_tehsil}
                    onChange={handleSelectChange}
                    required
                  >
                    <option value="">Select Tehsil</option>
                    {complaintTehsils
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((tehsil) => (
                        <option key={tehsil._id} value={tehsil._id}>
                          {tehsil.name}
                        </option>
                      ))}
                  </RegistrationSelect>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:clock" className="text-lg" />
                    Complaint Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    name="complaint_time"
                    min={minimumComplaintTime}
                    required
                    aria-describedby="complaint-time-help"
                    className="form-control cpf-bg"
                    value={formData.complaint_time}
                    onChange={handleInputChange}
                    onFocus={() => setMinimumComplaintTime(formatDateForInput(new Date()))}
                  />
                  <small id="complaint-time-help" style={{ display: 'block', marginTop: 6, color: '#718678', fontSize: 13 }}>Select the current or a future date and time.</small>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:file-document" className="text-lg" />
                    Complaint Detail
                  </label>
                  <textarea
                    name="complaint_detail"
                    className="form-control cpf-bg"
                    rows={4}
                    placeholder="Enter a description..."
                    value={formData.complaint_detail}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <label className="form-label cpf-lbl text-white d-flex align-items-center gap-2">
                    <Icon icon="mdi:note-text" className="text-lg" />
                    Additional Remarks
                  </label>
                  <textarea
                    name="complaint_additional_remarks"
                    className="form-control cpf-bg"
                    rows={4}
                    placeholder="Additional Remarks..."
                    value={formData.complaint_additional_remarks}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-12 col-md-12 col-sm-12 my-3">
              <label
                className="form-label d-flex text-md text-white align-items-center gap-2"
                style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)' }}
              >
                <input
                  type="checkbox"
                  checked={anonymize}
                  onChange={(e) => setAnonymize(e.target.checked)}
                  className="form-check-input"
                />
                <span><strong>Keep complainant information confidential</strong><small>Protect the complainant&apos;s identity during the complaint process.</small></span>
              </label>
            </div>
            <div className="col-lg-12 col-md-12 col-sm-12 my-3">
              <label
                className="form-label d-flex text-md text-white align-items-center gap-2"
                style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)' }}
              >
                <input
                  type="checkbox"
                  checked={informative}
                  onChange={(e) => setInformative(e.target.checked)}
                  className="form-check-input"
                />
                <span><strong>Record as an informative call</strong><small>For record only. This will not be dispatched and is excluded from dashboards, counts, and reports.</small></span>
              </label>
            </div>
            <div className="d-flex justify-content-end registration-footer">
              <span>Review the information before submitting.</span>
              <button
                className="btn btn-success text-sm btn-sm px-6 py-12 radius-12 my-24 mx-10 col-lg-2 col-md-4 col-sm-12"
                type="submit"
              >
                Submit complaint <ArrowRight size={18} className="ms-2" aria-hidden="true" />
              </button>
            </div>
          </div>
        </form>
      </div>
      )}
    </div>
  );
};

export default ComplainFormPage;
